import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { useCancelTopUp } from '../hooks/use-credits';
import type { OpenTopUp } from '../types';

/** A top-up that was started but not paid: pick the payment page up again, or drop the order. */
export const OpenCheckoutNotice = ({ openTopUp }: { openTopUp: OpenTopUp }) => {
  const { t } = useTranslation();
  const { mutate: cancel, isPending } = useCancelTopUp();
  const amount = formatMinor(openTopUp.amountMinor, openTopUp.currencyCode);

  return (
    <Alert
      role="status"
      data-testid="open-checkout-notice"
      className="border-amber-300/60 bg-amber-50 text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
    >
      <AlertTitle>
        {t('OPEN_TOPUP_TITLE', { amount, defaultValue: 'You have an unfinished top-up of {{amount}}' })}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p>{t('OPEN_TOPUP_TEXT', 'Continue where you left off, or cancel it to start over.')}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button className="w-full sm:w-auto" onClick={() => window.location.assign(openTopUp.checkoutUrl)}>
            {t('CONTINUE_CHECKOUT', 'Continue checkout')}
          </Button>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            loading={isPending}
            disabled={isPending}
            onClick={() => cancel(openTopUp.topUpId)}
          >
            {t('CANCEL_TOPUP', 'Cancel top-up')}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};
